/** @jsxImportSource @emotion/react */

import { useState, useRef } from 'react';
import { css } from '@emotion/react';
import { smoothAnimate, checkDocument, hasOwn } from '@buyhatke/custom-lib-js';

let openAnimationFrameRunner: ReturnType<typeof smoothAnimate>;
let closeAnimationFrameRunner: ReturnType<typeof smoothAnimate>;

interface QnA {
	q: React.ReactNode;
	a: React.ReactNode;
}

export const FaqItem = ({ q, a, lastItem }: QnA & { lastItem?: boolean }) => {
	const [isOpen, open] = useState(false);
	const ansBox = useRef<HTMLDivElement>(null);
	const opener = () => {
		const $ansBox = ansBox.current;

		if (!isOpen) {
			if (
				checkDocument &&
				typeof closeAnimationFrameRunner !== 'undefined' &&
				hasOwn(window, 'cancelAnimationFrame') &&
				'getRafVal' in closeAnimationFrameRunner
			) {
				window.cancelAnimationFrame(
					closeAnimationFrameRunner.getRafVal()
				);
			}
			$ansBox.classList.remove('h-0');
			const height = $ansBox.offsetHeight;
			$ansBox.classList.add('h-0');
			openAnimationFrameRunner = smoothAnimate(
				(h: number) => {
					$ansBox.style.height = `${h}px`;
				},
				0,
				height,
				0,
				0.1,
				() => {
					$ansBox.style.height = null;
					$ansBox.classList.remove('h-0');
				}
			);
		} else {
			if (checkDocument && hasOwn(window, 'cancelAnimationFrame')) {
				window.cancelAnimationFrame(
					openAnimationFrameRunner.getRafVal()
				);
			}

			const height = $ansBox.offsetHeight;

			closeAnimationFrameRunner = smoothAnimate(
				(h: number) => {
					$ansBox.style.height = `${h}px`;
				},
				height,
				0,
				height,
				0.1,
				() => {
					$ansBox.style.height = null;
					$ansBox.classList.add('h-0');
				}
			);
		}
		open(!isOpen);
	};

	return (
		<li
			className={`label-block py-4 ${
				lastItem ? '' : 'border-b border-gray-300'
			}`}
		>
			<div
				className={`border border-transparent select-none p-3 flex items-start justify-start yellow-icon cursor-pointer ${
					isOpen ? 'border-gray-300 bg-white shadow rounded-lg' : ''
				}`}
				css={css`
					transition: background 0.1s ease-out;
				`}
				onClick={opener}
				onKeyUp={e => {
					if (e.code === 'Space' || e.code === 'Enter') {
						opener();
					}
				}}
				role="button"
				tabIndex={0}
			>
				<img
					src="https://bitbns.com/assets/img/appIcons/svg/faq_down.svg"
					alt="v"
					className="rounded-full border border-orange-300 mt-1 inline-block"
					width="23"
					css={css`
						transition: transform 0.1s ease-out;
						transform: rotate(${isOpen ? '0deg' : '-90deg'});
					`}
				/>

				<h4 className="leading-relaxed ml-4 mt-1">{q}</h4>
			</div>
			<div
				ref={ansBox}
				className="faq-ans mr-4 ml-16 overflow-hidden h-0"
			>
				<div className="faq-details my-4 leading-relaxed">{a}</div>
			</div>
		</li>
	);
};
